import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import PageLayout from '../components/PageLayout';
import Pagination from '../components/Pagination';
import axios from 'axios';
import AdBanner from '../components/AdBanner';
import NativeAdCard from '../components/NativeAdCard';
import { useAds, injectAds } from '../hooks/useAds';
import {
  Plane, Search, Globe, Filter, MapPin, Users, X, ExternalLink,
  Building2, Calendar, ChevronRight, ArrowLeft, Wifi
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SEOHead from '../components/SEOHead';

import { API } from '../config';

const ALLIANCE_COLORS = {
  'Star Alliance': 'bg-[#1E3A8A]/15 text-[#1E3A8A] border-[#1E3A8A]/30',
  'Oneworld': 'bg-[#FF4500]/15 text-[#FF4500] border-[#FF4500]/30',
  'SkyTeam': 'bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30',
  'Independent': 'bg-muted text-muted-foreground border-border',
};

const ALLIANCE_DOT = {
  'Star Alliance': 'bg-[#1E3A8A]',
  'Oneworld': 'bg-[#FF4500]',
  'SkyTeam': 'bg-[#10B981]',
  'Independent': 'bg-muted-foreground',
};

// Airline logo using clearbit or similar - fallback to IATA code badge
function AirlineLogo({ airline, size = 'md' }) {
  const [imgError, setImgError] = useState(false);
  const sizeClass = size === 'lg' ? 'w-16 h-16 text-xl' : 'w-10 h-10 text-sm';
  const domain = airline.website
    ? airline.website.replace('https://', '').replace('http://', '').replace('www.', '').split('/')[0]
    : null;

  if (!imgError && domain) {
    return (
      <div className={`${sizeClass} rounded-xl overflow-hidden bg-white dark:bg-gray-800 border border-border flex items-center justify-center flex-shrink-0 p-1`}>
        <img
          src={`https://logo.clearbit.com/${domain}`}
          alt={airline.name}
          className="w-full h-full object-contain"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  return (
    <div
      className={`${sizeClass} rounded-xl flex items-center justify-center flex-shrink-0 font-bold`}
      style={{ background: `${airline.logo_color || '#1E3A8A'}18`, color: airline.logo_color || '#1E3A8A', fontFamily: 'Space Grotesk' }}
    >
      {airline.iata_code}
    </div>
  );
}

export default function Airlines() {
  const [airlines, setAirlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [alliance, setAlliance] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [selected, setSelected] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailRef = useRef(null);
  const LIMIT = 24;
  const ads = useAds('airlines');

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [alliance]);

  const fetchAirlines = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (alliance && alliance !== 'all') params.append('alliance', alliance);
      const res = await axios.get(`${API}/airlines?${params}`);
      setAirlines(res.data.airlines);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, alliance]);

  useEffect(() => { fetchAirlines(); }, [fetchAirlines]);

  const openAirline = async (airline) => {
    // Toggle off if already selected
    if (selected?.iata_code === airline.iata_code) {
      setSelected(null);
      setDetailData(null);
      return;
    }
    setSelected(airline);
    setDetailData(null);
    setDetailLoading(true);
    // On mobile, scroll to detail panel
    setTimeout(() => {
      detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    try {
      const res = await axios.get(`${API}/airlines/${airline.iata_code}`);
      setDetailData(res.data);
    } catch {
      /* silent */
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetailData(null);
  };

  const currentAirline = detailData?.airline || selected;

  return (
    <PageLayout>
      <SEOHead
        title="Airlines Directory — All IATA Codes"
        description="Browse 128+ airlines worldwide with IATA codes, alliances, hub airports, and route information. Find your airline and connect with fellow passengers."
        url="/airlines"
        keywords="airlines IATA codes, Star Alliance, Oneworld, SkyTeam, airline directory, all airlines"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Plane className="w-6 h-6 text-[#FF4500]" />
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Airlines Directory</h1>
          </div>
          <p className="text-muted-foreground">{total > 0 ? `${total} airlines worldwide` : 'Loading airlines...'}</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search airline, code, or country..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="airline-search-input"
            />
          </div>
          <Select value={alliance} onValueChange={setAlliance}>
            <SelectTrigger className="w-48">
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              <SelectValue placeholder="Filter by alliance" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Alliances</SelectItem>
              <SelectItem value="Star Alliance">Star Alliance</SelectItem>
              <SelectItem value="Oneworld">Oneworld</SelectItem>
              <SelectItem value="SkyTeam">SkyTeam</SelectItem>
              <SelectItem value="Independent">Independent</SelectItem>
            </SelectContent>
          </Select>
          {(search || alliance !== 'all') && (
            <Button variant="outline" onClick={() => { setSearch(''); setAlliance('all'); }}>
              <X className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Alliance legend */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[['Star Alliance', 'Star Alliance'], ['Oneworld', 'Oneworld'], ['SkyTeam', 'SkyTeam'], ['Independent', 'Independent']].map(([name, val]) => (
            <button key={name} onClick={() => setAlliance(alliance === val ? 'all' : val)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                alliance === val ? 'ring-2 ring-offset-1 ring-[#FF4500] border-[#FF4500]/40' : 'bg-muted/50 hover:bg-muted border-border'
              }`}>
              <span className={`w-2.5 h-2.5 rounded-full ${ALLIANCE_DOT[name]}`} />
              {name}
            </button>
          ))}
        </div>

        {/* Main layout: list + detail panel */}
        <div className={`flex gap-6 transition-all ${selected ? 'flex-col lg:flex-row' : 'flex-col'}`}>

          {/* Airlines Grid */}
          <div className={`${selected ? 'lg:w-[55%]' : 'w-full'} transition-all duration-300`}>
            {loading ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {[...Array(12)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
              </div>
            ) : airlines.length === 0 ? (
              <div className="text-center py-16">
                <Plane className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No airlines found</p>
              </div>
            ) : (
              <>
                <div className={`grid gap-3 mb-6 ${selected ? 'grid-cols-1 sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
                  {injectAds(airlines, ads, 2).map((item, i) =>
                    item.__isAd
                      ? <NativeAdCard key={`ad-${item.ad_id}-${i}`} ad={item} variant="airline" />
                      : (
                    <motion.div key={item.iata_code} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.015 }}>
                      <Card
                        className={`fly-card p-3.5 cursor-pointer hover:shadow-lg transition-all h-full group ${
                          selected?.iata_code === item.iata_code
                            ? 'ring-2 ring-[#FF4500] border-[#FF4500]/40'
                            : 'hover:border-[#FF4500]/30'
                        }`}
                        onClick={() => openAirline(item)}
                        data-testid="airline-card"
                      >
                        <div className="flex items-start gap-2.5 mb-2">
                          <AirlineLogo airline={item} size="md" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold leading-tight truncate group-hover:text-[#FF4500] transition-colors" title={item.name}>
                              {item.name}
                            </p>
                            <p className="text-xs text-muted-foreground">{item.flag} {item.country}</p>
                          </div>
                          <ChevronRight className={`w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5 transition-transform ${
                            selected?.iata_code === item.iata_code ? 'rotate-90 text-[#FF4500]' : 'group-hover:translate-x-0.5'
                          }`} />
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge className={`text-[10px] ${ALLIANCE_COLORS[item.alliance] || ALLIANCE_COLORS.Independent}`}>
                            {item.alliance}
                          </Badge>
                          {item.hub_airports?.slice(0, 2).map(hub => (
                            <span key={hub} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{hub}</span>
                          ))}
                        </div>
                        {item.description && (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-2 leading-relaxed">{item.description}</p>
                        )}
                      </Card>
                    </motion.div>
                      )
                  )}
                </div>

                <Pagination
                  page={page}
                  pages={pages}
                  total={total}
                  limit={LIMIT}
                  onPageChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); closeDetail(); }}
                  data-testid="airlines-pagination"
                />
              </>
            )}
          </div>

          {/* Airline Detail Panel */}
          <AnimatePresence>
            {selected && (
              <motion.div
                ref={detailRef}
                key={selected.iata_code}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.25 }}
                className="lg:w-[45%] lg:sticky lg:top-24 lg:self-start"
                data-testid="airline-detail-panel"
              >
                <Card className="fly-card overflow-hidden">
                  {/* Detail Header */}
                  <div className="bg-gradient-to-br from-[#111827] to-[#1E3A8A]/80 p-5 text-white relative">
                    <button
                      onClick={closeDetail}
                      className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                      data-testid="close-airline-detail"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center overflow-hidden p-1 flex-shrink-0">
                        {currentAirline && (
                          <AirlineLogo airline={currentAirline} size="lg" />
                        )}
                      </div>
                      <div>
                        <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
                          {currentAirline?.name}
                        </h2>
                        <p className="text-white/70 text-sm">{currentAirline?.flag} {currentAirline?.country}</p>
                        <Badge className={`mt-1.5 text-[10px] ${ALLIANCE_COLORS[currentAirline?.alliance] || ALLIANCE_COLORS.Independent}`}>
                          {currentAirline?.alliance}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {detailLoading ? (
                    <div className="p-4 space-y-3">
                      {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 rounded-xl" />)}
                    </div>
                  ) : (
                    <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">

                      {/* Key stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                        {[
                          ['IATA Code', currentAirline?.iata_code, 'text-[#FF4500] font-bold text-lg'],
                          ['ICAO Code', currentAirline?.icao_code, ''],
                          ['Founded', currentAirline?.founded, ''],
                          ['Fleet Size', currentAirline?.fleet_size ? `~${currentAirline.fleet_size}` : 'N/A', ''],
                          ['Country', currentAirline?.country, ''],
                          ['Alliance', currentAirline?.alliance, ''],
                        ].map(([label, value, valueClass]) => (
                          <div key={label} className="fly-card p-2.5 rounded-xl">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
                            <p className={`text-sm font-semibold mt-0.5 ${valueClass}`} style={label === 'IATA Code' ? { fontFamily: 'Space Grotesk' } : {}}>
                              {value || 'N/A'}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Website */}
                      {currentAirline?.website && (
                        <a
                          href={currentAirline.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-3 rounded-xl bg-[#FF4500]/8 border border-[#FF4500]/20 hover:bg-[#FF4500]/12 transition-colors group"
                          data-testid="airline-website-link"
                        >
                          <Globe className="w-4 h-4 text-[#FF4500] flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground">Official Website</p>
                            <p className="text-sm text-[#FF4500] font-medium truncate group-hover:underline">
                              {currentAirline.website.replace('https://', '').replace('http://', '')}
                            </p>
                          </div>
                          <ExternalLink className="w-3.5 h-3.5 text-[#FF4500] flex-shrink-0" />
                        </a>
                      )}

                      {/* Description */}
                      {currentAirline?.description && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">About</p>
                          <p className="text-sm leading-relaxed text-foreground">{currentAirline.description}</p>
                        </div>
                      )}

                      <Separator />

                      {/* Hub Airports */}
                      {currentAirline?.hub_airports?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5" /> Hub Airports
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {currentAirline.hub_airports.map(hub => (
                              <Link key={hub} to={`/airport/${hub}`}
                                className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-[#1E3A8A]/10 text-[#1E3A8A] hover:bg-[#1E3A8A]/20 transition-colors font-medium"
                              >
                                <MapPin className="w-3 h-3" />
                                {hub}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Recent Flights */}
                      {detailData?.flights?.length > 0 && (
                        <div>
                          <Separator className="mb-4" />
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                            <Plane className="w-3.5 h-3.5" /> Recent Flights
                          </p>
                          <div className="space-y-2">
                            {detailData.flights.slice(0, 5).map(f => (
                              <Link key={f.flight_id} to={`/flight/flight_${f.flight_number}`}>
                                <div className="flex items-center justify-between p-2.5 rounded-xl hover:bg-muted transition-colors">
                                  <div className="flex items-center gap-2.5 text-sm">
                                    <span className="font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>{f.flight_number}</span>
                                    <span className="text-muted-foreground flex items-center gap-1">
                                      {f.origin_code} <Plane className="w-2.5 h-2.5" /> {f.destination_code}
                                    </span>
                                  </div>
                                  <Badge className={`text-[10px] ${f.status === 'on_time' ? 'status-on-time' : f.status === 'boarding' ? 'status-boarding' : 'status-delayed'}`}>
                                    {f.status?.replace('_', ' ')}
                                  </Badge>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* CTA */}
                      <div className="pt-1">
                        <Button
                          className="fly-btn-primary w-full"
                          asChild
                        >
                          <Link to={currentAirline?.hub_airports?.[0] ? `/airport/${currentAirline.hub_airports[0]}` : '/discover'}>
                            <Plane className="w-4 h-4 mr-2" />
                            Visit {currentAirline?.hub_airports?.[0] || 'Hub'} Airport Community
                          </Link>
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </PageLayout>
  );
}
